import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import D3EFeature from "../models/D3EFeature";
import ListWrapper from "../utils/ListWrapper";
import ResultStatus from "../classes/ResultStatus";
import BuilderProject from "../models/BuilderProject";
import D3EAppTemplate from "../models/D3EAppTemplate";
import TextView from "./TextView";
import PageNavigator from "../classes/PageNavigator";
import Result from "../classes/Result";
import FieldValidation from "../utils/FieldValidation";
import PopupWrapperView from "./PopupWrapperView";
import LabelWithStringField from "./LabelWithStringField";
import { Runnable } from "../classes/core";
import { BuildContext } from "../classes/BuildContext";

type _CancelButtonOnPressed = (d3eState: BuildProjectViewRefs) => void;

type _ButtonRefOnPressed = (d3eState: BuildProjectViewRefs) => void;

type _NameFieldOnChanged = (
  text: string,
  d3eState: BuildProjectViewRefs
) => void;

type _EmailFieldOnChanged = (
  text: string,
  d3eState: BuildProjectViewRefs
) => void;

type _WebsiteFieldOnChanged = (
  text: string,
  d3eState: BuildProjectViewRefs
) => void;

type _PhoneFieldOnChanged = (
  text: string,
  d3eState: BuildProjectViewRefs
) => void;

type _PromptFiledOnChanged = (
  text: string,
  d3eState: BuildProjectViewRefs
) => void;

export interface BuildProjectViewProps extends BaseUIProps {
  key?: string;
  obj: BuilderProject;
  templates: Array<D3EAppTemplate>;
  features: Array<D3EFeature>;
  _templatesHash?: number;
  _featuresHash?: number;
}
/// To store state data for BuildProjectView
class BuildProjectViewRefs {
  public buttonRef: ButtonRefState = new ButtonRefState();
  public cancelButton: CancelButtonState = new CancelButtonState();
  promptFiledController: ui.TextEditingController =
    new ui.TextEditingController();
  promptFiledFocusNode: ui.FocusNode = new ui.FocusNode();
}

interface ButtonRefWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuildProjectViewRefs;
  _onAskAIHandler?: _ButtonRefOnPressed;
}

class ButtonRefState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _ButtonRefWithState extends ObservableComponent<ButtonRefWithStateProps> {
  buttonRefFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ButtonRefWithStateProps) {
    super(props);

    this.initState();
  }
  public get buttonRef(): ButtonRefState {
    return this.props.d3eState.buttonRef;
  }
  public get d3eState(): BuildProjectViewRefs {
    return this.props.d3eState;
  }
  public get _onAskAIHandler(): _ButtonRefOnPressed {
    return this.props._onAskAIHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("buttonRef", null, this.buttonRef);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["buttonRef", "buttonRef.", "buttonRef.focus", "buttonRef.hover"],
      this.rebuild
    );
  }
  public buttonRefOnEnter(event): void {
    return this.buttonRef.setHover(true);
  }
  public buttonRefOnExit(event): void {
    return this.buttonRef.setHover(false);
  }
  public buttonRefOnFocusChange(val): void {
    return this.buttonRef.setFocus(val);
  }
  public dispose(): void {
    this.buttonRef.setHover(false);

    this.buttonRef.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      constraints: new ui.BoxConstraints({ minWidth: 90 }),
      height: 38,
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.buttonRef.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.buttonRef.focus
          ? cStyle.tButtonPrimaryButtonDecorationOnFocus
          : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.buttonRef.disable,
        onPressed: () => {
          this._onAskAIHandler(this.d3eState);
        },
        onFocusChange: (val) => {
          this.buttonRefOnFocusChange(val);
        },
        child: TextView({
          data: "Submit",
          style: new ui.TextStyle({ fontSize: 13 }),
          className: "x44",
        }),
        onEnter: (event) => {
          this.buttonRefOnEnter(event);
        },
        onExit: (event) => {
          this.buttonRefOnExit(event);
        },
      }),
      className: "x343 vc",
    });
  }
}
function ButtonRefWithState(props: ButtonRefWithStateProps) {
  return React.createElement(_ButtonRefWithState, props);
}

interface CancelButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuildProjectViewRefs;
  _onCancelHandler?: _CancelButtonOnPressed;
}

class CancelButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _CancelButtonWithState extends ObservableComponent<CancelButtonWithStateProps> {
  cancelButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: CancelButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get cancelButton(): CancelButtonState {
    return this.props.d3eState.cancelButton;
  }
  public get d3eState(): BuildProjectViewRefs {
    return this.props.d3eState;
  }
  public get _onCancelHandler(): _CancelButtonOnPressed {
    return this.props._onCancelHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("cancelButton", null, this.cancelButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "cancelButton",
        "cancelButton.",
        "cancelButton.focus",
        "cancelButton.hover",
      ],
      this.rebuild
    );
  }
  public cancelButtonOnEnter(event): void {
    return this.cancelButton.setHover(true);
  }
  public cancelButtonOnExit(event): void {
    return this.cancelButton.setHover(false);
  }
  public cancelButtonOnFocusChange(val): void {
    return this.cancelButton.setFocus(val);
  }
  public dispose(): void {
    this.cancelButton.setHover(false);

    this.cancelButton.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      constraints: new ui.BoxConstraints({ minWidth: 90 }),
      margin: ui.EdgeInsets.symmetric({
        horizontal: 10.0,
        vertical: 0.0,
        transitions: new Map(),
      }),
      child: Button({
        padding: cStyle.tButtonSecondaryButtonPaddingOn,
        decoration: this.cancelButton.hover
          ? cStyle.tButtonSecondaryButtonDecorationOnHover
          : this.cancelButton.focus
          ? cStyle.tButtonSecondaryButtonDecorationOnFocus
          : cStyle.tButtonSecondaryButtonDecorationOn,
        disable: this.cancelButton.disable,
        onPressed: () => {
          this._onCancelHandler(this.d3eState);
        },
        onFocusChange: (val) => {
          this.cancelButtonOnFocusChange(val);
        },
        child: TextView({
          data: "Cancel",
          style: new ui.TextStyle({ fontSize: 13 }),
          className: "x58",
        }),
        onEnter: (event) => {
          this.cancelButtonOnEnter(event);
        },
        onExit: (event) => {
          this.cancelButtonOnExit(event);
        },
      }),
      className: "x8a",
    });
  }
}
function CancelButtonWithState(props: CancelButtonWithStateProps) {
  return React.createElement(_CancelButtonWithState, props);
}

class _BuildProjectViewState extends ObservableComponent<BuildProjectViewProps> {
  static defaultProps = { obj: null, templates: [], features: [] };
  d3eState: BuildProjectViewRefs = new BuildProjectViewRefs();
  message: string = "";
  private _editInitListeners: Array<Runnable> = [];
  private _d3eModelErrors: Array<string> = [];
  private _nameErrors: Array<string> = [];
  private _emailErrors: Array<string> = [];
  private _projectDetailsErrors: Array<string> = [];
  private _contactErrors: Array<string> = [];
  private _websiteErrors: Array<string> = [];
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: BuildProjectViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): BuilderProject {
    return this.props.obj;
  }
  public get templates(): Array<D3EAppTemplate> {
    return this.props.templates;
  }
  public get features(): Array<D3EFeature> {
    return this.props.features;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.subscribeToList(this.templates, "templates");

    this.updateSyncCollProperty("templates", this.props.templates);

    this.subscribeToList(this.features, "features");

    this.updateSyncCollProperty("features", this.props.features);

    this.on(
      [
        "contactErrors",
        "emailErrors",
        "nameErrors",
        "obj",
        "obj.contact",
        "obj.email",
        "obj.name",
        "obj.projectDetails",
        "obj.website",
        "websiteErrors",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: BuildProjectViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }

    if (prevProps.templates !== this.props.templates) {
      this.updateObservableColl(
        "templates",
        prevProps.templates,
        this.props.templates
      );

      this.fire("templates", this);
    }

    if (prevProps.features !== this.props.features) {
      this.updateObservableColl(
        "features",
        prevProps.features,
        this.props.features
      );

      this.fire("features", this);
    }
  }
  public setMessage(val: string): void {
    let isValChanged: boolean = this.message !== val;

    if (!isValChanged) {
      return;
    }

    this.message = val;

    this.fire("message", this);
  }
  public get d3eModelErrors(): Array<string> {
    return this._d3eModelErrors ?? [];
  }
  public set d3eModelErrors(val: Array<string>) {
    this._d3eModelErrors.clear();

    this._d3eModelErrors.addAll(val);
  }
  public get nameErrors(): Array<string> {
    return this._nameErrors ?? [];
  }
  public set nameErrors(val: Array<string>) {
    this._nameErrors.clear();

    this._nameErrors.addAll(val);
  }
  public async computeNameErrors(): Promise<void> {
    this.nameErrors = [];

    let it: string = this.obj.name;

    if (it == null || it === "") {
      this.nameErrors.add("Name cannot be empty.");
    }

    this.fire("nameErrors", this);
  }
  public get emailErrors(): Array<string> {
    return this._emailErrors ?? [];
  }
  public set emailErrors(val: Array<string>) {
    this._emailErrors.clear();

    this._emailErrors.addAll(val);
  }
  public async computeEmailErrors(): Promise<void> {
    this.emailErrors = [];

    let it: string = this.obj.email;

    if (it == null || it === "") {
      this.emailErrors.add("Email cannot be empty.");
    }

    if (!FieldValidation.isEmail(it)) {
      this.emailErrors.add("Email is Invalid!");
    }

    this.fire("emailErrors", this);
  }
  public get projectDetailsErrors(): Array<string> {
    return this._projectDetailsErrors ?? [];
  }
  public set projectDetailsErrors(val: Array<string>) {
    this._projectDetailsErrors.clear();

    this._projectDetailsErrors.addAll(val);
  }
  public async computeProjectDetailsErrors(): Promise<void> {
    this.projectDetailsErrors = [];

    let it: string = this.obj.projectDetails;

    if (it == null || it === "") {
      this.projectDetailsErrors.add("ProjectDetails cannot be empty.");
    }

    this.fire("projectDetailsErrors", this);
  }
  public get contactErrors(): Array<string> {
    return this._contactErrors ?? [];
  }
  public set contactErrors(val: Array<string>) {
    this._contactErrors.clear();

    this._contactErrors.addAll(val);
  }
  public async computeContactErrors(): Promise<void> {
    this.contactErrors = [];

    let it: string = this.obj.contact;

    if (it == null || it === "") {
      this.contactErrors.add("Contact cannot be empty.");
    }

    this.fire("contactErrors", this);
  }
  public get websiteErrors(): Array<string> {
    return this._websiteErrors ?? [];
  }
  public set websiteErrors(val: Array<string>) {
    this._websiteErrors.clear();

    this._websiteErrors.addAll(val);
  }
  public async computeWebsiteErrors(): Promise<void> {
    this.websiteErrors = [];

    let it: string = this.obj.website;

    if (it == null || it === "") {
      this.websiteErrors.add("website cannot be empty.");
    }

    this.fire("websiteErrors", this);
  }
  public setEditDefaults(): void {}
  public get isEditValid(): boolean {
    if (this.nameErrors.isNotEmpty) {
      return false;
    }

    if (this.emailErrors.isNotEmpty) {
      return false;
    }

    if (this.projectDetailsErrors.isNotEmpty) {
      return false;
    }

    if (this.contactErrors.isNotEmpty) {
      return false;
    }

    if (this.websiteErrors.isNotEmpty) {
      return false;
    }

    return true;
  }
  public validate(): Array<string> {
    let errors: Array<string> = [];

    this.computeNameErrors();

    errors.addAll(this.nameErrors);

    this.computeEmailErrors();

    errors.addAll(this.emailErrors);

    this.computeProjectDetailsErrors();

    errors.addAll(this.projectDetailsErrors);

    this.computeContactErrors();

    errors.addAll(this.contactErrors);

    this.computeWebsiteErrors();

    errors.addAll(this.websiteErrors);

    return errors;
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return PopupWrapperView({
      title: "Build Project",
      content: ui.Column({
        crossAxisAlignment: ui.CrossAxisAlignment.start,
        mainAxisAlignment: ui.MainAxisAlignment.start,
        children: [
          LabelWithStringField({
            name: "Project Name",
            placeHolder: "Enter Name",
            value: this.obj.name,
            errors: this.nameErrors,
            onChanged: (text) => {
              this.nameFieldonChanged(text, this.d3eState);
            },
            className: "x12 hc h",
            key: "0",
          }),
          LabelWithStringField({
            name: "Emial",
            placeHolder: "Enter Email",
            value: this.obj.email,
            errors: this.emailErrors,
            onChanged: (text) => {
              this.emailFieldonChanged(text, this.d3eState);
            },
            className: "x34 hc h",
            key: "1",
          }),
          LabelWithStringField({
            name: "Website",
            placeHolder: "Enter Website",
            value: this.obj.website,
            errors: this.websiteErrors,
            onChanged: (text) => {
              this.websiteFieldonChanged(text, this.d3eState);
            },
            className: "x2c hc h",
            key: "2",
          }),
          LabelWithStringField({
            name: "Contact Number",
            placeHolder: "Enter Contact Number",
            value: this.obj.contact,
            errors: this.contactErrors,
            onChanged: (text) => {
              this.phoneFieldonChanged(text, this.d3eState);
            },
            className: "x32 hc h",
            key: "3",
          }),
          TextView({ data: "Project Details", className: "x7d hc", key: "4" }),
          ui.InputField({
            value: this.obj.projectDetails,
            placeHolder: "Enter Project Details",
            maxLines: 8,
            keyboardType: ui.TextInputType.multiline,
            style: new ui.TextStyle({ color: cStyle.c6 }),
            controller: this.d3eState.promptFiledController,
            onChanged: (text) => {
              this.promptFiledonChanged(text, this.d3eState);
            },
            onFocusChange: (val) => {},
            focusNode: this.d3eState.promptFiledFocusNode,
            className: "x6f hc vc",
            key: "5",
          }),
        ],
        className: "x40 hc h",
      }),
      buttons: [
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              children: [
                CancelButtonWithState({
                  d3eState: this.d3eState,
                  _onCancelHandler: this.onCancelHandler,
                  key: "0",
                }),
                ButtonRefWithState({
                  d3eState: this.d3eState,
                  _onAskAIHandler: this.onAskAIHandler,
                  key: "1",
                }),
              ],
              className: "xcc",
              key: "0",
            }),
          ],
          key: "0",
        }),
      ],
      className: ui.join(this.props.className, "BuildProjectView hc vc"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onAskAIHandler = async (
    d3eState: BuildProjectViewRefs
  ): Promise<void> => {
    this.obj.setTemplates(this.templates);

    this.obj.setFeatures(this.features);

    if (this.validate().isEmpty) {
      let result: Result<BuilderProject> = await this.obj.save();

      if (result.status === ResultStatus.Success) {
        this.navigator.pushContactSupport();
      }
    }
  };
  public onCancelHandler = (d3eState: BuildProjectViewRefs): void => {
    this.navigator.close();
  };
  public nameFieldonChanged = (
    val: string,
    d3eState: BuildProjectViewRefs
  ): void => {
    this.obj.setName(val);
  };
  public emailFieldonChanged = (
    val: string,
    d3eState: BuildProjectViewRefs
  ): void => {
    this.obj.setEmail(val);
  };
  public websiteFieldonChanged = (
    val: string,
    d3eState: BuildProjectViewRefs
  ): void => {
    this.obj.setWebsite(val);
  };
  public phoneFieldonChanged = (
    val: string,
    d3eState: BuildProjectViewRefs
  ): void => {
    this.obj.setContact(val);
  };
  public promptFiledonChanged = (
    val: string,
    d3eState: BuildProjectViewRefs
  ): void => {
    this.obj.setProjectDetails(val);
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get buttonRef() {
    return this.d3eState.buttonRef;
  }
  public get cancelButton() {
    return this.d3eState.cancelButton;
  }
}
export default function BuildProjectView(props: BuildProjectViewProps) {
  return React.createElement(
    _BuildProjectViewState,
    { ..._BuildProjectViewState.defaultProps, ...props },
    ListWrapper.fromInput<D3EAppTemplate>(props.templates, "templates"),
    ListWrapper.fromInput<D3EFeature>(props.features, "features")
  );
}
